import ReactGA from 'react-ga4';
import { useEffect, useRef, useState } from 'react';
/////////////////////////////////////////////////////////////
import { telegramWebApp } from '@/main';
import { useCustomDispatch, useCustomSelector } from '@/core/redux/hooks';
import {
	LevelUp,
	Coins,
	NinjaTap,
	NinjaProgress,
	LastTimeIncremented,
	CoinsAnimation,
	CoinsInfo,
	DashboardHeader,
	ModalInfo,
} from '@/app/shared/components';
import { useAnimatedCounter } from '@/app/hooks';
import {
	setVisiblePendingCoinsDrawer,
	setVisibleLevelUpDrawer,
	setIsAirdropUser,
} from '@/core/redux/slices';
import './Dashboard.css';

const Dashboard: React.FC = () => {
	/*********************
	// #region Constants
	**********************/

	/*********************
	// #region Redux
	**********************/
	const {
		level,
		last_time_incremented,
		visiblePendingCoinsDrawer,
		visibleLevelUpDrawer,
		counter,
	} = useCustomSelector((state) => state.counter);
	const { isAirdropUser } = useCustomSelector((state) => state.user);

	/*********************
	// #region Hooks
	**********************/
	const coinsRef = useRef<HTMLDivElement>(null);
	const dispatch = useCustomDispatch();
	const [animationCounterTrigger, _] = useState<boolean>(false);
	const counterAnimation = useAnimatedCounter(
		counter,
		counter + last_time_incremented,
		1000,
		animationCounterTrigger
	);

	/*********************
	// #region Constants
	**********************/
	const deviceHeight = telegramWebApp.viewportHeight;

	/*********************
	// #region States
	**********************/
	const [animateCoins, setAnimateCoins] = useState<boolean>(false);
	const [isExpanded, setIsExpanded] = useState<boolean>(false);
	const [visibleDrawerLevelUp, setVisibleDrawerLevelUp] =
		useState<boolean>(false);
	const [
		visibleDrawerLastTimeIncremented,
		setVisibleDrawerLastTimeIncremented,
	] = useState<boolean>(false);
	const [visibleInitialModalInfo, setVisibleInitialModalInfo] =
		useState<boolean>(isAirdropUser);

	/********************* 
	// #region Observables
	**********************/
	useEffect(() => {
		ReactGA.send({
			hitType: 'pageview',
			page: '/dashboard',
			title: 'Dashboard',
		});
	}, []);
	useEffect(() => {
		if (visibleLevelUpDrawer) {
			setVisibleDrawerLevelUp(true);
		}
	}, [level]);

	useEffect(() => {
		if (visiblePendingCoinsDrawer && last_time_incremented > 0) {
			setTimeout(() => {
				setIsExpanded(true);
				setVisibleDrawerLastTimeIncremented(true);
			}, 400);
		}
	}, [last_time_incremented]);

	/*********************
	// #region Functions  
	**********************/
	const handleAnimationCollectedCoins = () => {
		setVisibleDrawerLastTimeIncremented(false);
		dispatch(setVisiblePendingCoinsDrawer(false));
		setAnimateCoins(true);

		setTimeout(() => {
			setAnimateCoins(false);
		}, 1000);
	};

	const handleCloseDrawerLevelUp = () => {
		setVisibleDrawerLevelUp(false);
		dispatch(setVisibleLevelUpDrawer(false));
	};

	const handleCloseModalInfo = () => {
		setVisibleInitialModalInfo(false);
		dispatch(setIsAirdropUser(false));
	};

	/*********************
	// #region Template
	**********************/
	return (
		<div
			className="flex flex-col"
			style={{
				minHeight: `calc(${deviceHeight}px - 205px)`,
			}}
		>
			<DashboardHeader />

			<CoinsInfo />
			<div ref={coinsRef} className="counter">
				<Coins isAnimated={animateCoins} counter={counterAnimation} />
			</div>
			<NinjaTap />
			<NinjaProgress />

			{visibleDrawerLevelUp && (
				<LevelUp
					isOpen={visibleDrawerLevelUp}
					onClose={handleCloseDrawerLevelUp}
				/>
			)}

			{visibleDrawerLastTimeIncremented &&
				isExpanded &&
				!visibleInitialModalInfo && (
					<LastTimeIncremented
						isOpen={visibleDrawerLastTimeIncremented}
						onClose={handleAnimationCollectedCoins}
					/>
				)}

			{isAirdropUser && (
				<ModalInfo
					isOpen={visibleInitialModalInfo}
					onClose={handleCloseModalInfo}
				/>
			)}

			<CoinsAnimation animateCoins={animateCoins} coinsRef={coinsRef} />
		</div>
	);
};

export default Dashboard;
